import { cx, css } from 'emotion';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from '@hig/theme-context';
import { ControlBehavior } from '@hig/behaviors';

var commonjsGlobal = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

/** `Object#toString` result references. */
var funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]';

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Detect free variable `global` from Node.js. */
var freeGlobal = (typeof commonjsGlobal === 'undefined' ? 'undefined' : _typeof(commonjsGlobal)) == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = (typeof self === 'undefined' ? 'undefined' : _typeof(self)) == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Checks if `value` is a host object in IE < 9.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
 */
function isHostObject(value) {
  // Many host objects are `Object` objects that can coerce to strings
  // despite having improperly defined `toString` methods.
  var result = false;
  if (value != null && typeof value.toString != 'function') {
    try {
      result = !!(value + '');
    } catch (e) {}
  }
  return result;
}

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' + funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');

/* Built-in method references that are verified to be native. */
var Map = getNative(root, 'Map'),
    nativeCreate = getNative(Object, 'create');

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) || isHostObject(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && maskSrcKey in func;
}

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to process.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return func + '';
    } catch (e) {}
  }
  return '';
}

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 8-9 which returns 'object' for typed array and other constructors.
  var tag = isObject(value) ? objectToString.call(value) : '';
  return tag == funcTag || tag == genTag;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value === 'undefined' ? 'undefined' : _typeof(value);
  return !!value && (type == 'object' || type == 'function');
}

/**
 * Returns a css className that combines what the user passes down and a string.
 * Ideally the string would map to the element's js style object's name.
 * @param {string} className
 * @param {string} appendedString
 * @returns {string} A string that looks like className__appendedString
 */

function createCustomClassNames(className, appendedString) {
  return className && className.split(" ").reduce(function (acc, cur) {
    return cx(acc, cur.trim() + "__" + appendedString);
  }, "");
}

function stylesheet(props, themeData) {
  var isPressed = props.isPressed,
      hasFocus = props.hasFocus,
      hasHover = props.hasHover,
      disabled = props.disabled,
      customStylesheet = props.stylesheet;

  var opacity = disabled ? themeData["colorScheme.opacity.disabled"] : "1.0";
  // because we don't have access to the checked status in react, we need to
  // write css rules to handle both cases
  var borderColor = hasFocus && !hasHover ? themeData["checkbox.unchecked.focus.borderColor"] : themeData["checkbox.unchecked.default.borderColor"];
  var checkedInnerShadow = "inset 0 0 0 " + themeData["checkbox.pressed.haloWidth"] + " " + themeData["checkbox.checked.default.backgroundColor"];
  var uncheckedInnerShadow = "inset 0 0 0 " + themeData["checkbox.borderWidth"] + " \n    " + borderColor + "\n  ";
  var outerShadow = "0 0 0 0px transparent";

  if (disabled) {
    // do nothing
  } else if (isPressed) {
    outerShadow = "0 0 0 " + themeData["checkbox.pressed.haloWidth"] + " " + themeData["checkbox.pressed.haloColor"];
  } else if (hasHover) {
    outerShadow = "0 0 0 " + themeData["checkbox.hover.haloWidth"] + " " + themeData["checkbox.hover.haloColor"];
  } else if (hasFocus) {
    outerShadow = "0 0 0 " + themeData["checkbox.focus.haloWidth"] + " " + themeData["checkbox.focus.haloColor"];
  }

  var styles = {
    radioButtonContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      alignItems: "center"
    },

    radioButtonWrapper: {
      opacity: opacity,
      position: "relative",
      color: "transparent",
      border: "none",
      backgroundColor: "transparent",
      textAlign: "center",
      fontSize: "25px",
      lineHeight: "14px",
      height: themeData["checkbox.minHeight"],
      width: themeData["checkbox.minWidth"],
      borderRadius: "50%",
      boxShadow: uncheckedInnerShadow + "," + outerShadow,
      transitionDuration: "0.3s, 0.3s",
      transitionProperty: "box-shadow, background-color",
      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1), cubic-bezier(0.4, 0, 0.2, 1)"
    },

    // this maps to .radio-button__input
    radioButtonInput: {
      display: "inline-block",
      opacity: "0",
      position: "absolute",
      width: "100%",
      height: "100%",
      top: "0",
      left: "0",
      margin: "0",
      padding: "0",
      zIndex: "1",

      "&:checked + span": {
        boxShadow: checkedInnerShadow + "," + outerShadow
      }
    }
  };

  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var ButtonPresenter = function (_Component) {
  _inherits(ButtonPresenter, _Component);

  function ButtonPresenter() {
    _classCallCheck(this, ButtonPresenter);

    return _possibleConstructorReturn(this, (ButtonPresenter.__proto__ || Object.getPrototypeOf(ButtonPresenter)).apply(this, arguments));
  }

  _createClass(ButtonPresenter, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          customStylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties(_props, ["stylesheet"]);

      var className = otherProps.className;

      var radioButtonWrapperClassName = createCustomClassNames(className, "radio-button-wrapper");

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var resolvedRoles = _ref.resolvedRoles,
              metadata = _ref.metadata;

          var styles = stylesheet(_extends({
            stylesheet: customStylesheet
          }, _this2.props), resolvedRoles, metadata.colorSchemeId);
          return React.createElement("span", {
            className: cx(css(styles.radioButtonWrapper), radioButtonWrapperClassName)
          });
        }
      );
    }
  }]);

  return ButtonPresenter;
}(Component);

ButtonPresenter.propTypes = {
  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func
};
ButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ButtonPresenter",
  "props": {
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    }
  }
};

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var RadioButtonPresenter = function (_Component) {
  _inherits$1(RadioButtonPresenter, _Component);

  function RadioButtonPresenter() {
    _classCallCheck$1(this, RadioButtonPresenter);

    return _possibleConstructorReturn$1(this, (RadioButtonPresenter.__proto__ || Object.getPrototypeOf(RadioButtonPresenter)).apply(this, arguments));
  }

  _createClass$1(RadioButtonPresenter, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          disabled = _props.disabled,
          hasFocus = _props.hasFocus,
          hasHover = _props.hasHover,
          isPressed = _props.isPressed,
          customStylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties$1(_props, ["disabled", "hasFocus", "hasHover", "isPressed", "stylesheet"]);

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var resolvedRoles = _ref.resolvedRoles;

          var styles = stylesheet(_extends$1({
            isPressed: isPressed,
            hasFocus: hasFocus,
            hasHover: hasHover,
            disabled: disabled,
            stylesheet: customStylesheet
          }, _this2.props), resolvedRoles);

          var className = otherProps.className;

          var radioButtonInputClassName = createCustomClassNames(className, "radio-button-input");

          return React.createElement(
            "div",
            { className: cx(css(styles.radioButtonContainer), className) },
            React.createElement("input", _extends$1({}, otherProps, {
              disabled: disabled,
              type: "radio",
              className: cx(css(styles.radioButtonInput), radioButtonInputClassName)
            })),
            React.createElement(ButtonPresenter, _extends$1({}, otherProps, {
              hasFocus: hasFocus,
              hasHover: hasHover,
              isPressed: isPressed,
              disabled: disabled,
              stylesheet: customStylesheet
            }))
          );
        }
      );
    }
  }]);

  return RadioButtonPresenter;
}(Component);

RadioButtonPresenter.propTypes = {
  /**
   * Called when user moves focus from the field
   */
  onBlur: PropTypes.func,
  /**
   * Called when user changes the value of the field
   */
  onChange: PropTypes.func,
  /**
   * Called when user clicks on the field
   */
  onClick: PropTypes.func,
  /**
   * Called when user puts focus on the field
   */
  onFocus: PropTypes.func,
  /**
   * Returns whether or not the button is currently focused
   */
  hasFocus: PropTypes.bool,
  /**
   * Returns whether or not the button is currently hovered
   */
  hasHover: PropTypes.bool,
  /**
   * Returns whether or not the button is currently pressed
   */
  isPressed: PropTypes.bool,
  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func
};
RadioButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "RadioButtonPresenter",
  "props": {
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user moves focus from the field"
    },
    "onChange": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user changes the value of the field"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user clicks on the field"
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user puts focus on the field"
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Returns whether or not the button is currently focused"
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Returns whether or not the button is currently hovered"
    },
    "isPressed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Returns whether or not the button is currently pressed"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function RadioButton(props) {
  var onBlur = props.onBlur,
      onFocus = props.onFocus,
      onMouseDown = props.onMouseDown,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      onMouseUp = props.onMouseUp,
      otherProps = _objectWithoutProperties$2(props, ["onBlur", "onFocus", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp"]);

  return React.createElement(
    ControlBehavior,
    {
      onBlur: onBlur,
      onFocus: onFocus,
      onMouseDown: onMouseDown,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onMouseUp: onMouseUp
    },
    function (_ref) {
      var hasFocus = _ref.hasFocus,
          hasHover = _ref.hasHover,
          isPressed = _ref.isPressed,
          handleBlur = _ref.onBlur,
          handleFocus = _ref.onFocus,
          handleMouseDown = _ref.onMouseDown,
          handleMouseEnter = _ref.onMouseEnter,
          handleMouseLeave = _ref.onMouseLeave,
          handleMouseUp = _ref.onMouseUp;
      return React.createElement(RadioButtonPresenter, _extends$2({
        hasFocus: hasFocus,
        hasHover: hasHover,
        isPressed: isPressed,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMouseDown: handleMouseDown,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseUp: handleMouseUp
      }, otherProps));
    }
  );
}
RadioButton.__docgenInfo = {
  "description": "",
  "displayName": "RadioButton"
};

export default RadioButton;
